import { ReactNode } from 'react';
import { MoonLoader } from 'react-spinners';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { Colors } from '../../styles/colors';

export type ButtonTheme = 'filled' | 'outlined' | 'secondary' | 'text' | 'danger' | 'champagne';

interface IButtonProps {
  theme?: ButtonTheme;
  text: string;
  onClick?: () => void;
  buttonWidth?: string;
  buttonHeight?: string;
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  isDisabled?: boolean;
  marginTop?: number;
  marginBottom?: number;
  dataTestId?: string;
  fullWidth?: boolean;
  className?: string;
  /** Native button type. Left unset it stays `submit`, as before. */
  type?: 'button' | 'submit' | 'reset';
  /** Optional icon rendered before the label (16px). */
  leftIcon?: ReactNode;
}

const sizeStyles = {
  sm: css`
    height: 36px;
    padding: 0 14px;
    font-size: 13px;
    gap: 6px;
  `,
  md: css`
    height: 40px;
    padding: 0 18px;
    font-size: 14px;
    gap: 8px;
  `,
  lg: css`
    height: 48px;
    padding: 0 24px;
    font-size: 15px;
    gap: 8px;
  `,
};

const BaseButton = styled.button<{ $size?: 'sm' | 'md' | 'lg'; $fullWidth?: boolean }>`
  ${tw`
    outline-none
    font-semibold
    border
    border-solid
    focus:outline-none
    cursor-pointer
    inline-flex
    justify-center
    items-center
    whitespace-nowrap
    select-none
  `};
  border-radius: var(--radius-md);
  width: 100%;
  line-height: 1;
  letter-spacing: -0.01em;
  transition:
    background-color var(--transition-normal),
    border-color var(--transition-normal),
    color var(--transition-normal),
    box-shadow var(--transition-normal),
    transform var(--transition-fast);
  ${({ $size = 'md' }) => sizeStyles[$size]};
  ${({ $fullWidth }) => $fullWidth && tw`w-full`};

  &:focus-visible {
    outline: 2px solid ${Colors.Black};
    outline-offset: 2px;
    box-shadow: var(--shadow-focus);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    background-color: ${Colors.Gray100};
    color: ${Colors.Gray400};
    border-color: ${Colors.Gray200};
    box-shadow: none;
    cursor: not-allowed;
  }
`;

const FilledButton = styled(BaseButton)`
  background-color: ${Colors.Black};
  color: ${Colors.OnPrimary};
  border-color: ${Colors.Black};
  box-shadow: var(--shadow-xs);

  &:hover:not(:disabled) {
    background-color: ${Colors.BlackHover};
    border-color: ${Colors.BlackHover};
  }
`;

const OutlinedButton = styled(BaseButton)`
  background-color: ${Colors.BgPrimary};
  color: ${Colors.Black};
  border-color: ${Colors.Black};

  &:hover:not(:disabled) {
    background-color: ${Colors.BgSecondary};
  }
`;

/** Neutral secondary action: white surface, light border. */
const SecondaryButton = styled(BaseButton)`
  background-color: ${Colors.BgPrimary};
  color: ${Colors.TextPrimary};
  border-color: var(--color-border-strong);
  box-shadow: var(--shadow-xs);

  &:hover:not(:disabled) {
    background-color: ${Colors.BgSecondary};
    border-color: var(--color-border-strong);
  }
`;

const DangerButton = styled(BaseButton)`
  background-color: ${Colors.Error};
  color: #ffffff;
  border-color: ${Colors.Error};
  box-shadow: var(--shadow-xs);

  &:hover:not(:disabled) {
    background-color: #dc2626;
    border-color: #dc2626;
  }
`;

const ChampagneButton = styled(BaseButton)`
  background-color: ${Colors.Accent};
  color: ${Colors.OnPrimary};
  border-color: ${Colors.Accent};
  box-shadow: var(--shadow-xs);

  &:hover:not(:disabled) {
    filter: brightness(0.94);
  }
`;

const TextButton = styled(BaseButton)`
  background-color: transparent;
  color: ${Colors.Black};
  border-color: transparent;
  padding-left: 8px;
  padding-right: 8px;

  &:hover:not(:disabled) {
    background-color: ${Colors.BgSecondary};
  }

  &:disabled {
    background-color: transparent;
    border-color: transparent;
  }
`;

const ButtonContainer = styled.div<{
  $buttonWidth?: string;
  $marginTop?: number;
  $marginBottom?: number;
  $fullWidth?: boolean;
}>`
  ${tw`
    flex
    justify-center
    items-center
  `}
  width: ${({ $buttonWidth, $fullWidth }) => ($fullWidth ? '100%' : $buttonWidth || 'auto')};
  margin-top: ${({ $marginTop }) => ($marginTop ? `${$marginTop}px` : '0')};
  margin-bottom: ${({ $marginBottom }) => ($marginBottom ? `${$marginBottom}px` : '0')};
`;

const IconSlot = styled.span`
  ${tw`inline-flex items-center justify-center shrink-0`}
  width: 16px;
  height: 16px;
`;

function componentForTheme(theme: ButtonTheme) {
  switch (theme) {
    case 'outlined':
      return OutlinedButton;
    case 'secondary':
      return SecondaryButton;
    case 'text':
      return TextButton;
    case 'danger':
      return DangerButton;
    case 'champagne':
      return ChampagneButton;
    case 'filled':
    default:
      return FilledButton;
  }
}

export function Button(props: IButtonProps) {
  const {
    theme = 'filled',
    text,
    onClick,
    buttonWidth,
    buttonHeight,
    size = 'md',
    isLoading = false,
    isDisabled = false,
    marginTop = 0,
    marginBottom = 0,
    dataTestId,
    fullWidth = false,
    className,
    type,
    leftIcon,
  } = props;

  const Component = componentForTheme(theme);
  const onDark = theme === 'filled' || theme === 'danger' || theme === 'champagne';
  const loaderColor = onDark ? 'currentColor' : Colors.Gray500;

  return (
    <ButtonContainer
      $buttonWidth={buttonWidth}
      $marginTop={marginTop}
      $marginBottom={marginBottom}
      $fullWidth={fullWidth}
    >
      <Component
        onClick={onClick}
        disabled={isDisabled || isLoading}
        data-testid={dataTestId}
        $size={size}
        $fullWidth={fullWidth}
        className={className}
        style={buttonHeight ? { height: buttonHeight } : undefined}
        aria-busy={isLoading}
        type={type}
      >
        {isLoading ? (
          <MoonLoader color={loaderColor} size={14} />
        ) : (
          <>
            {leftIcon && <IconSlot aria-hidden>{leftIcon}</IconSlot>}
            {text}
          </>
        )}
      </Component>
    </ButtonContainer>
  );
}
